import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { CustomizedBox } from "components/Custom/CustomizedBox";
import { CustomizedTooltip } from "components/Custom/CustomizedTooltip";
import { useDisable } from "hooks/use-disable";
import { FieldError, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IDeliveryTrip } from "types/Logistic/deliveryTrip";
import EditIcon from "@mui/icons-material/Edit";
import ControlledTextField from "components/Controller/ControlledTextField";
import ControlledSelect from "components/Controller/ControlledSelect";
import CheckedVehicleModal from "components/UI/Modal/CheckedModal/CheckedVehicleModal";
import { useDeliveryTripModal } from "hooks/Logistic/DeliveryTrip/use-delivery-trip-modal";
import CheckedUserModal from "components/UI/Modal/CheckedModal/CheckedUserModal";
import { ICreatedBy } from "types/global";
import CheckedTrailerModal from "components/UI/Modal/CheckedModal/CheckedTrailerModal";
import { useSnackbar } from "notistack";
import { workListQueryFormatter } from "utils/Formatter/Logistic/DeliveryTrip";
import { useConfirmation } from "hooks/use-confirmation";
import VehicleConfirmation from "components/UI/Modal/VehicleConfirmation";
import { useEffect, useRef, useState } from "react";
import { useVehicleTypeOptions } from "hooks/Logistic/use-vehicle-type-options";

// 86d4b1mj3 / 86d4b1mkf — a cleared driver is an *empty* driver, never
// `undefined`. The name fields are `ControlledTextField`s bound to
// `primary_driver.name` / `secondary_driver.name`; hand them `undefined` and
// React treats the input as uncontrolled and stops managing it, so the old
// name stays painted after the user has removed the driver. An empty string
// keeps the input controlled and repaints. Everything that decides "is there
// a driver" (`ExpensesList`, the yup `rowHasDriver` guard, the picker
// pre-check) keys on `name` / `user_unique_id` being falsy, so an empty object
// reads as no driver everywhere. Same trap as the kilometer fields — see the
// comment on `applyVehicleTypeChange`.
const emptyDriver = (): ICreatedBy => ({
  user_unique_id: "",
  name: "",
  first_name: "",
  last_name: "",
  email: "",
  img_url: [],
});

const DeliveryTripVehicle = () => {
  const { options: typeOptions } = useVehicleTypeOptions();

  const {
    control,
    formState: { errors },
    getValues,
    reset,
    resetField,
    setValue,
  } = useFormContext<IDeliveryTrip>();

  // 86d4889jb — gates the vehicle picker and seeds its default filter.
  const selectedVehicleType = useWatch({ control, name: "vehicle.type" });

  // 86d4889jb — the type the rest of the รถ section is currently consistent
  // with. Updated in an effect, which runs after render, so inside the
  // dropdown's onChange (which fires during the event, before React
  // re-renders) this still holds the PREVIOUS type. That is what lets us tell
  // "user picked a different type" from "the value was written for us".
  const settledVehicleTypeRef = useRef<string>("");

  useEffect(() => {
    settledVehicleTypeRef.current = selectedVehicleType ?? "";
  }, [selectedVehicleType]);

  // 86d4889jb — changing ประเภทรถ invalidates everything below it. The vehicle
  // that was chosen is of the old type, and so are the trailer and drivers
  // that came with it, so they are cleared rather than left contradicting the
  // field above them.
  //
  // `work_list` is rebuilt too, and that is not optional: shipping cost is
  // looked up per vehicle type (`location.shipping_cost.find(c => c.type ===
  // vehicleType)`), so leaving it alone would keep money priced against a type
  // the document no longer has.
  //
  // Hooked to the dropdown's `onChange` rather than a watcher on purpose:
  // `vehicleChangeHandler` writes `vehicle.type` when a vehicle is picked, and
  // a watcher could not tell that apart — it would wipe the vehicle the user
  // had just selected.
  const applyVehicleTypeChange = async (nextType: string) => {
    const workList = getValues("work_list");
    const newWorkList = await workListQueryFormatter(workList, nextType);

    reset((prev) => ({
      ...prev,
      vehicle: {
        type: nextType,
        vehicle_no: "",
        license_plate: "",
      },
      vehicle_id: undefined,
      trailer_id: undefined,
      trailer: {
        trailer_no: "",
        license_plate: "",
      },
      primary_driver: emptyDriver(),
      secondary_driver: emptyDriver(),
      // `start_kilometer` came from the vehicle's odometer, so it goes with
      // it. `total_kilometer_distance` is derived from it and is only ever
      // recomputed by the two fields' own onChange handlers — nothing watches
      // them — so clearing the start without clearing the total would leave a
      // distance on screen that was measured against a vehicle this document
      // no longer has. `end_kilometer` is the user's own reading and stays.
      //
      // Both are set to `0`, not `undefined`. These render through
      // `ControlledNumberTextField` (NumericFormat): handing a controlled
      // input `undefined` makes React treat it as uncontrolled and stop
      // managing it, so the previous number stays painted in the DOM even
      // though form state cleared. `0` keeps the input controlled and actually
      // repaints. It also matches what the fields' own onChange handlers
      // already write when there is nothing to compute from.
      start_kilometer: 0,
      total_kilometer_distance: 0,
      work_list: newWorkList,
    }));
  };

  const vehicleTypeChangeHandler = async (event: any) => {
    const nextType = event?.target?.value ?? "";
    const previousType = settledVehicleTypeRef.current;

    // First pick on a blank document clears nothing — there is no vehicle
    // underneath yet. Re-picking the same value is a no-op too.
    if (!previousType || previousType === nextType) {
      return;
    }

    await applyVehicleTypeChange(nextType);
  };

  const { enqueueSnackbar } = useSnackbar();

  const [vehicleData, setVehicleData] = useState<any>();

  // 86d37db3t — status-derived vars removed. `disabledStatus` below no
  // longer keys on status, and no other logic here needed it.
  const [disabled] = useDisable();

  const { t } = useTranslation();

  const { vehicle, primaryDriver, secondaryDriver, trailer } =
    useDeliveryTripModal();

  const vehicleChangeHandler = async (vehicleData: any) => {
    if (vehicleData) {
      const {
        id,
        type,
        unique_id,
        register_id,
        trailer_id,
        trailer_unique_id,
        trailer_register_id,
        driver_object_list,
        current_kilometer,
      } = vehicleData;
      const vehicleObject = {
        type,
        vehicle_no: unique_id,
        license_plate: register_id,
      };

      const primaryDriverObject =
        driver_object_list &&
        driver_object_list.length > 0 &&
        driver_object_list[0];

      let formatPrimaryDriver: ICreatedBy | undefined;

      if (primaryDriverObject) {
        const { user_id, ...otherPrimary } = primaryDriverObject;

        formatPrimaryDriver = {
          ...otherPrimary,
          user_unique_id: user_id,
          name:
            driver_object_list[0].first_name +
            " " +
            driver_object_list[0].last_name,
        };
      }

      const secondaryDriverObject =
        driver_object_list &&
        driver_object_list.length > 1 &&
        driver_object_list[1];

      let formatSecondaryDriver: ICreatedBy | undefined;

      if (secondaryDriverObject) {
        const { user_id, ...otherSecondary } = secondaryDriverObject;

        formatSecondaryDriver = {
          ...otherSecondary,
          user_unique_id: user_id,
          name:
            driver_object_list[1].first_name +
            " " +
            driver_object_list[1].last_name,
        };
      }

      const workList = getValues("work_list");

      const newWorkList = await workListQueryFormatter(
        workList,
        vehicleObject.type,
      );

      const endKilometer = getValues("end_kilometer");

      reset((prev) => ({
        ...prev,
        vehicle: vehicleObject,
        vehicle_id: id,
        trailer_id: trailer_id,
        trailer: {
          trailer_no: trailer_unique_id,
          license_plate: trailer_register_id,
        },
        primary_driver: formatPrimaryDriver ?? emptyDriver(),
        secondary_driver: formatSecondaryDriver ?? emptyDriver(),
        start_kilometer: current_kilometer,
        total_kilometer_distance: endKilometer
          ? endKilometer - current_kilometer
          : 0,
        work_list: newWorkList,
      }));
    } else {
      reset((prev) => ({
        ...prev,
        vehicle: {
          type: "",
          vehicle_no: "",
          license_plate: "",
        },
        vehicle_id: undefined,
        trailer_id: undefined,
        trailer: {
          trailer_no: "",
          license_plate: "",
        },
        primary_driver: emptyDriver(),
        secondary_driver: emptyDriver(),
        start_kilometer: 0,
        total_kilometer_distance: 0,
      }));
    }
  };

  const finishVehiclesSelect = async (vehicleData: any) => {
    const workList = getValues("work_list");
    if (workList.length > 0) {
      openVehicleConfirmation();
    } else {
      await vehicleChangeHandler(vehicleData);
    }
    setVehicleData(vehicleData);
  };

  const finishTrailersSelect = (data: any) => {
    if (data) {
      const { id, unique_id, register_id } = data;
      reset((prev) => ({
        ...prev,
        trailer_id: id,
        trailer: {
          trailer_no: unique_id,
          license_plate: register_id,
        },
      }));
    } else {
      reset((prev) => ({
        ...prev,
        trailer_id: undefined,
        trailer: {
          trailer_no: "",
          license_plate: "",
        },
      }));
    }
  };

  // Picking / unpicking a driver in the modal goes through `setValue`, never
  // `reset(...)`. `reset` replaces the whole form — it drops every registered
  // field (`_fields = {}`), clears the field-array registry and re-keys every
  // `useFieldArray` row — as a side effect of writing one object, and the
  // expenses rows sit in a field array right underneath. The driver-2 unpick
  // had already moved off `reset` for that reason (the old commented-out
  // line); the driver-1 unpick had not. That is the one difference between
  // the two paths QA compared in 86d4b1mkf (clearing driver 1 wiped driver
  // 2's income rows on dev, clearing driver 2 did not) — the wipe itself did
  // not reproduce in isolation, so `ExpensesList` also stopped trusting the
  // caller and now reconciles rows against the drivers that exist.
  const setDriver = (
    slot: "primary_driver" | "secondary_driver",
    driver: ICreatedBy,
  ) => {
    setValue(slot, driver, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    // The parent write above does not reach the `.name` text field on its
    // own — `useController` subscribes with `exact: true` — so the name is
    // written a second time under its own key.
    setValue(`${slot}.name`, driver.name ?? "", { shouldDirty: true });
  };

  const clearDriver = (slot: "primary_driver" | "secondary_driver") => {
    setDriver(slot, emptyDriver());
    resetField(`${slot}.name`, { defaultValue: "" });
  };

  const finishPrimaryDriverSelect = (data: any) => {
    if (data) {
      const secondaryDriver = getValues("secondary_driver");

      const { unique_id, first_name, last_name, email, img_url } = data;

      if (secondaryDriver && secondaryDriver.user_unique_id === unique_id) {
        enqueueSnackbar("พนักงานขนส่งไม่สามารถซ้ำกันภายในเอกสาร", {
          variant: "error",
        });
      } else {
        const formatData = {
          user_unique_id: unique_id,
          name: `${first_name} ${last_name}`,
          first_name,
          last_name,
          email,
          img_url,
        };
        setDriver("primary_driver", formatData);
      }
    } else {
      clearDriver("primary_driver");
    }
  };

  const finishSecondaryDriverSelect = (data: any) => {
    if (data) {
      const primaryDriver = getValues("primary_driver");

      const { unique_id, first_name, last_name, email, img_url } = data;

      if (primaryDriver && primaryDriver.user_unique_id === unique_id) {
        enqueueSnackbar("พนักงานขนส่งไม่สามารถซ้ำกันภายในเอกสาร", {
          variant: "error",
        });
      } else {
        const formatData = {
          user_unique_id: unique_id,
          name: `${first_name} ${last_name}`,
          first_name,
          last_name,
          email,
          img_url,
        };
        setDriver("secondary_driver", formatData);
      }
    } else {
      clearDriver("secondary_driver");
    }
  };

  const {
    confirmation: vehicleConfirmation,
    openConfirmationHandler: openVehicleConfirmation,
    closeConfirmationHandler: closeVehicleConfirmation,
    submitConfirmationHandler: submitVehicleConfirmation,
  } = useConfirmation(async () => await vehicleChangeHandler(vehicleData));

  // 86d37db3t — no per-status disable in Edit mode. Vehicle/trailer/driver
  // pickers stay usable in every สถานะ; View Mode (`disabled`) is the only
  // lock. Kept as a named const so all downstream usages read cleanly.
  const disabledStatus = false;

  return (
    <>
      <CustomizedBox>
        <Box display={"flex"} alignItems={"center"} mb={1} gap={1}>
          <Typography fontWeight={600}>
            {t("logistic.delivery_trip.vehicle.index")}
          </Typography>
          {!disabled && !disabledStatus && (
            // 86d4889jb — always enabled. `ประเภทรถ` seeds the modal's default
            // filter but does not gate the button: with no type chosen the
            // picker simply opens unfiltered, which is a better answer than a
            // dead button. Only View Mode hides it.
            <CustomizedTooltip title="เลือกรถ">
              <IconButton
                onClick={vehicle.openModalHandler}
                color="primary"
                size="small"
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </CustomizedTooltip>
          )}
        </Box>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={12} md={6}>
            <ControlledSelect
              label={t("logistic.delivery_trip.vehicle.vehicle_type")}
              control={control}
              name="vehicle.type"
              options={typeOptions}
              onChange={vehicleTypeChangeHandler}
              error={Boolean(errors?.vehicle?.type)}
              helperText={(errors?.vehicle?.type as FieldError)?.message}
              viewMode={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <ControlledTextField
              label={t("logistic.delivery_trip.vehicle.vehicle_no")}
              control={control}
              name="vehicle.vehicle_no"
              error={Boolean(errors?.vehicle?.vehicle_no)}
              helperText={errors?.vehicle?.vehicle_no?.message}
              disabled
              viewMode={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <ControlledTextField
              label={t("logistic.delivery_trip.vehicle.license_plate")}
              control={control}
              name="vehicle.license_plate"
              error={Boolean(errors?.vehicle?.license_plate)}
              helperText={errors?.vehicle?.license_plate?.message}
              disabled
              viewMode={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <ControlledTextField
              control={control}
              name="trailer.license_plate"
              label={t("logistic.delivery_trip.trailer.license_plate")}
              error={Boolean(errors?.trailer?.license_plate)}
              InputProps={{
                endAdornment: !disabled && !disabledStatus && (
                  <InputAdornment position="end">
                    <CustomizedTooltip
                      title={`เลือก${t(
                        "logistic.delivery_trip.trailer.license_plate",
                      )}`}
                    >
                      <IconButton
                        onClick={trailer.openModalHandler}
                        color="primary"
                        size="small"
                      >
                        <EditIcon
                          sx={{
                            fontSize: 16,
                          }}
                        />
                      </IconButton>
                    </CustomizedTooltip>
                  </InputAdornment>
                ),
              }}
              disabled
              viewMode={disabled}
            />
          </Grid>
        </Grid>
        <Typography fontWeight={600} my={1}>
          {t("logistic.delivery_trip.primary_driver.index")}
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={12} md={6}>
            <ControlledTextField
              control={control}
              name="primary_driver.name"
              label={t("logistic.delivery_trip.primary_driver.index")}
              error={Boolean(errors.primary_driver?.name)}
              helperText={errors.primary_driver?.name?.message}
              InputProps={{
                endAdornment: !disabled && !disabledStatus && (
                  <InputAdornment position="end">
                    <CustomizedTooltip
                      title={`เลือก${t(
                        "logistic.delivery_trip.primary_driver.index",
                      )}`}
                    >
                      <IconButton
                        onClick={primaryDriver.openModalHandler}
                        color="primary"
                        size="small"
                      >
                        <EditIcon
                          sx={{
                            fontSize: 16,
                          }}
                        />
                      </IconButton>
                    </CustomizedTooltip>
                  </InputAdornment>
                ),
              }}
              disabled
              viewMode={disabled}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <ControlledTextField
              control={control}
              name="secondary_driver.name"
              label={t("logistic.delivery_trip.secondary_driver.index")}
              error={Boolean(errors.secondary_driver?.name)}
              helperText={errors.secondary_driver?.name?.message}
              InputProps={{
                endAdornment: !disabled && !disabledStatus && (
                  <InputAdornment position="end">
                    <CustomizedTooltip
                      title={`เลือก${t(
                        "logistic.delivery_trip.secondary_driver.index",
                      )}`}
                    >
                      <IconButton
                        onClick={secondaryDriver.openModalHandler}
                        color="primary"
                        size="small"
                      >
                        <EditIcon
                          sx={{
                            fontSize: 16,
                          }}
                        />
                      </IconButton>
                    </CustomizedTooltip>
                  </InputAdornment>
                ),
              }}
              disabled
              viewMode={disabled}
            />
          </Grid>
        </Grid>
      </CustomizedBox>
      <CheckedVehicleModal
        // 86d4889jb — opens pre-filtered to the chosen type. The user can
        // clear the filter inside the modal to see every vehicle.
        defaultTypeFilter={selectedVehicleType}
        showSelectVehicle={vehicle.modal}
        vehicleIds={vehicle.ids}
        setVehicleIds={vehicle.setIdsSnapshot}
        vehicleIdsSnapshot={vehicle.idsSnapshot}
        setVehicleIdsSnapshot={vehicle.setIdsSnapshot}
        rowSelection="single"
        closeVehicleTable={vehicle.closeModalHandler}
        finishVehiclesSelect={finishVehiclesSelect}
      />
      <CheckedTrailerModal
        showSelectTrailer={trailer.modal}
        trailerId={trailer.ids}
        setTrailerId={trailer.setIdsSnapshot}
        trailerIdSnapshot={trailer.idsSnapshot}
        setTrailerIdSnapshot={trailer.setIdsSnapshot}
        closeTrailerTable={trailer.closeModalHandler}
        finishTrailerSelect={finishTrailersSelect}
        trailerOnly={false}
      />
      <CheckedUserModal
        showSelectUser={primaryDriver.modal}
        userIds={primaryDriver.ids}
        setUserIds={primaryDriver.setIdsSnapshot}
        userIdsSnapshot={primaryDriver.idsSnapshot}
        setUserIdsSnapshot={primaryDriver.setIdsSnapshot}
        rowSelection="single"
        closeUserTable={primaryDriver.closeModalHandler}
        finishUsersSelect={finishPrimaryDriverSelect}
        isDriver
      />
      <CheckedUserModal
        showSelectUser={secondaryDriver.modal}
        userIds={secondaryDriver.ids}
        setUserIds={secondaryDriver.setIdsSnapshot}
        userIdsSnapshot={secondaryDriver.idsSnapshot}
        setUserIdsSnapshot={secondaryDriver.setIdsSnapshot}
        rowSelection="single"
        closeUserTable={secondaryDriver.closeModalHandler}
        finishUsersSelect={finishSecondaryDriverSelect}
        isDriver
      />
      <VehicleConfirmation
        confirmation={vehicleConfirmation}
        closeConfirmationHandler={closeVehicleConfirmation}
        submitConfirmationHandler={submitVehicleConfirmation}
      />
    </>
  );
};

export default DeliveryTripVehicle;
